import { useState, useEffect, Dispatch, SetStateAction } from 'react';

// Drop-in replacement for useState that mirrors the value into
// sessionStorage on every change and rehydrates on mount. Per-tab, survives
// reload within the tab and back/forward navigation; cleared when the tab
// closes. Used to keep filter / search / pagination state alive when the
// user clicks into a record and hits back.
//
// `override` (when provided) wins over both sessionStorage and `initial`.
// It's how the cross-search bridge from /propiedades injects URL-carried
// filters: the URL is the source of truth on that one navigation, even if
// sessionStorage holds an older value from earlier in the tab.
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// Merge a persisted plain object over the default so a value written by an
// OLDER bundle (before new fields existed) still exposes every field the
// current code expects. Skipping this white-screened /propiedades when new
// filter code read `advancedFilters.bedrooms.length` on a stale persisted
// shape (GlitchTip intver2-web #568/#569). Non-object values pass through.
export function rehydrateSessionValue<T>(parsed: unknown, initial: T): T {
  if (isPlainObject(initial) && isPlainObject(parsed)) {
    return { ...initial, ...parsed } as T;
  }
  return parsed as T;
}

export function useSessionState<T>(
  key: string,
  initial: T,
  override?: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    if (override !== undefined) return override;
    try {
      const stored = sessionStorage.getItem(key);
      if (stored != null) return rehydrateSessionValue(JSON.parse(stored), initial);
    } catch {
      // sessionStorage may be disabled (private mode quotas, etc.). Fall
      // through to the default — the page just won't persist this session.
    }
    return initial;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Quota exceeded or storage disabled; nothing useful we can do here.
    }
  }, [key, state]);

  return [state, setState];
}
